import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AdvancedTargetGroup,
  AdvancedTargetGroupCategory,
  AdvancedTargetGroupService,
} from '../../../../woo_services.module/AdvancedTargetGroupService';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { RoutingService } from '../../../../woo_services.module/RoutingService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductFormat, Publisher, SlotType, wooId } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

type IncludedProductFormatSlotTypes = Record<wooId, Record<SlotType, boolean>>;

@Component({
  selector: 'edit-advanced-target-groups',
  templateUrl: './edit-advanced-target-groups.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  styles: ['.no-content { padding: 0.5rem }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditAdvancedTargetGroup implements OnInit {
  @Input() categories: AdvancedTargetGroupCategory[] = [];
  @Output() onChanges = new EventEmitter<void>();
  readonly staticFields = 2;
  readonly tableHeaders = [
    'Publicist',
    'Placering',
    'Preroll',
    'Midroll',
    'Postroll',
    'Preroll',
    'Midroll',
    'Postroll',
  ];

  advTargetGroup = signal<AdvancedTargetGroup | null>(null);
  publishers = signal<Publisher[]>([]);
  productFormats = signal<ProductFormat[]>([]);
  inRows = signal<InputRow[]>([]);
  updating = signal<boolean>(false);
  includedProductFormatSlotTypesSig = signal<IncludedProductFormatSlotTypes | null>(null);
  loading = signal<boolean>(true);

  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private advancedTargetGroupService: AdvancedTargetGroupService,
    private utilityService: ProductsPageUtilityService,
    private route: ActivatedRoute,
    private routingService: RoutingService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.advancedTargetGroupService
      .getTargetGroup(this.routingService.getParam(this.route, 'id'))
      .then((advancedTargetGroup) => {
        return this.advTargetGroup.set(advancedTargetGroup);
      })
      .then(() => {
        return this.publisherService.getPublishers(ExtraPublisherField.enabled).then((publishers) => {
          this.publishers.set(publishers.filter((p) => p.enabled));
        });
      })
      .then(async () => {
        return this.productFormatService
          .getProductFormats(ExtraProductFormatField.slotTypes, ExtraProductFormatField.enabled)
          .then((productFormats) => {
            this.productFormats.set(productFormats.filter((f) => f.enabled));
            this.includedProductFormatSlotTypesSig.set(
              this.utilityService.setIncludedProductFormatSlotTypes(this.productFormats()),
            );
            return this.buildInputRows();
          });
      })
      .finally(() => {
        this.loading.set(false);
        this.changeDetection.markForCheck();
      });
  }

  abort(): void {
    this.routingService.navigate(['/products', 'advanced_target_groups']);
  }

  async updateAdvancedTargetGroup(): Promise<void> {
    this.updating.set(true);
    this.advancedTargetGroupService
      .updateTargetGroup(this.advTargetGroup())
      .then(() => {
        this.routingService.navigate(['/products'], {
          queryParams: { tab: 'avancerade_malgrupper' },
          state: { deviceGroupEstimatesChanged: true },
        });
      })
      .finally(() => {
        this.updating.set(false);
      });
  }

  translateProductFormat(product: string): string {
    return this.translationService.convertProductFormat(product);
  }

  private buildInputRows(): InputRow[] {
    let inputRow: InputRow[] = [];

    this.publishers().map((publisher) => {
      this.utilityService.getPublisherSpecificProductFormats(publisher, this.productFormats()).map((productFormat) => {
        inputRow = [
          ...inputRow,
          {
            productFormat: productFormat.name,
            productFormatId: productFormat.id,
            publisherId: publisher.id,
            publisher: publisher.name,
            sharesPreroll: this.advTargetGroup().shares[publisher.id][productFormat.id].preroll,
            sharesMidroll: this.advTargetGroup().shares[publisher.id][productFormat.id].midroll,
            sharesPostroll: this.advTargetGroup().shares[publisher.id][productFormat.id].postroll,
            withdrawalLimitsPreroll: this.advTargetGroup().withdrawal_limits[publisher.id][productFormat.id].preroll,
            withdrawalLimitsMidroll: this.advTargetGroup().withdrawal_limits[publisher.id][productFormat.id].midroll,
            withdrawalLimitsPostroll: this.advTargetGroup().withdrawal_limits[publisher.id][productFormat.id].postroll,
          },
        ];
      });
    });
    inputRow.sort((a, b) => (a.publisher > b.publisher ? 0 : 1));
    this.inRows.set(inputRow);
    return inputRow;
  }
}

interface InputRow {
  publisher: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId: wooId;
  sharesPreroll?: number;
  sharesMidroll?: number;
  sharesPostroll?: number;
  withdrawalLimitsPreroll?: number;
  withdrawalLimitsMidroll?: number;
  withdrawalLimitsPostroll?: number;
}
